import CircuitRowCheckbox from '@/components/distributors/modals/CircuitRowCheckbox';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Translator } from '../translator.type';

export type PublishedCircuit = {
  id: string;
  code: number;
  name: string;
  remarks?: string;
  durationDays: number;
  durationNights: number;
  countries: string[];
  updatedAt: string;
  _count: {
    sharedCircuits: number;
  };
  agenciesCount?: number;
  distributedAt?: string;
  createdAt?: string;
};

export const CIRCUIT_SELECTION_COLUMNS = (
  t: Translator,
): ColumnDef<PublishedCircuit>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllRowsSelected() ||
          (table.getIsSomeRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => <CircuitRowCheckbox row={row} />,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'code',
    header: t('circuits.columns.code'),
    cell: ({ row }) => (
      <span>C{row.original.code.toString().padStart(4, '0')}</span>
    ),
  },
  {
    accessorKey: 'name',
    header: t('circuits.columns.circuit'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="nom"
          >
            {row.original.name}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="nom complet"
          >
            {row.original.name}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'remarks',
    header: t('circuits.columns.remarks'),
    cell: ({ row }) => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis"
            aria-label="remarques"
          >
            {row.original.remarks || '-'}
          </TooltipTrigger>
          <TooltipContent
            className="bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-lg"
            aria-label="remarques completes"
          >
            {row.original.remarks || '-'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    accessorKey: 'countries',
    header: t('circuits.columns.country'),
    cell: ({ row }) => <span>{row.original.countries.join(', ')}</span>,
  },
  {
    accessorKey: 'durationDays',
    header: t('circuits.columns.duration'),
    cell: ({ row }) =>
      row.original.durationDays ? (
        <span>
          {row.original.durationDays}j
          {row.original.durationNights && ` / ${row.original.durationNights}n`}
        </span>
      ) : null,
  },
  {
    accessorKey: 'updatedAt',
    header: t('circuits.columns.updatedAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Date de proposition',
    cell: ({ row }) => (
      <span>
        {row.original.createdAt
          ? format(new Date(row.original.createdAt), 'dd/MM/yyyy')
          : '-'}
      </span>
    ),
  },
  {
    accessorKey: 'distributedAt',
    header: 'Date de distribution',
    cell: ({ row }) => (
      <span>
        {row.original.distributedAt
          ? format(new Date(row.original.distributedAt), 'dd/MM/yyyy')
          : '-'}
      </span>
    ),
  },
];
